import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import PlayerIframe from '../components/PlayerIframe';
import { fetchEventDetails, getLiveStreamUrl } from '../services/api';
import { Event } from '../types';
import useMetaTags from '../hooks/useMetaTags';
import { RICH_SNIPPETS } from '../config/seo.config';
import { useFavorites } from '../hooks/useFavorites';
import { useToast } from '../context/ToastContext';
import { getCategoryLabel, getStatusLabel, shareEvent } from '../utils/eventUtils';
import SportIcon from '../components/icons/SportIcon';
import styles from './EventDetail.module.css';

interface RouteParams {
    id: string;
}

const EventDetail: React.FC = () => {
    const { id } = useParams<RouteParams>();
    const history = useHistory();
    const { isFavorite, toggleFavorite } = useFavorites();
    const { showToast } = useToast();

    const [event, setEvent] = useState<Event | null>(null);
    const [loading, setLoading] = useState(true);
    const [streamLoading, setStreamLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [streamUrl, setStreamUrl] = useState<string | null>(null);
    const [showPlayer, setShowPlayer] = useState(false);

    useMetaTags({
        title: event
            ? `${event.name} - ${event.category === 'Cricket' ? 'ক্রিকেট' : 'ফুটবল'} লাইভ | BDLINK LIVE`
            : 'ইভেন্ট বিস্তারিত | BDLINK LIVE',
        description: event
            ? `${event.name} লাইভ দেখুন। ${event.teamA || ''} বনাম ${event.teamB || ''} — ${event.location}`
            : 'লাইভ স্পোর্টস স্ট্রিমিং',
        url: `https://bdlink.live/event/${id}`,
        canonicalUrl: `https://bdlink.live/event/${id}`,
        locale: 'bn_BD',
        robots: 'index, follow',
        structuredData: event
            ? RICH_SNIPPETS.sportEvent({
                  name: event.name,
                  description: `${event.name} — ${event.location}`,
                  startDate: event.date,
                  endDate: event.date,
                  location: event.location,
                  teamA: event.teamA,
                  teamB: event.teamB,
              })
            : undefined,
    });

    useEffect(() => {
        const loadEvent = async () => {
            try {
                setLoading(true);
                const data = await fetchEventDetails(id);
                if (data) {
                    setEvent(data);
                    setError(null);
                } else {
                    setError('ইভেন্ট পাওয়া যায়নি');
                }
            } catch {
                setError('ইভেন্ট লোড করতে ব্যর্থ');
            } finally {
                setLoading(false);
            }
        };
        loadEvent();
    }, [id]);

    const handlePlayClick = async () => {
        try {
            setStreamLoading(true);
            setError(null);
            const { url, message } = await getLiveStreamUrl(id);
            if (url) {
                setStreamUrl(url);
                setShowPlayer(true);
            } else {
                const apiReason = message ? ` (${message})` : '';
                setError(
                    `লাইভ স্ট্রিম এখন উপলব্ধ নয়${apiReason}। ম্যাচ শেষ হয়ে থাকতে পারে বা স্ট্রিম সাময়িকভাবে বন্ধ।`
                );
                setShowPlayer(false);
            }
        } catch {
            setError('স্ট্রিম লোড করতে ব্যর্থ। আবার চেষ্টা করুন।');
        } finally {
            setStreamLoading(false);
        }
    };

    const handleShare = async () => {
        if (!event) return;
        const ok = await shareEvent(event);
        showToast(ok ? 'লিংক কপি/শেয়ার হয়েছে' : 'শেয়ার করতে ব্যর্থ', ok ? 'success' : 'error');
    };

    const handleFavorite = () => {
        if (!event) return;
        const was = isFavorite(event.id);
        toggleFavorite(event.id);
        showToast(was ? 'পছন্দ থেকে সরানো হয়েছে' : 'পছন্দে যোগ হয়েছে', 'success');
    };

    if (loading) {
        return (
            <div className={styles.loadingPage}>
                <div className={styles.spinner} />
                <p>ম্যাচ লোড হচ্ছে...</p>
            </div>
        );
    }

    if (!event) {
        return (
            <div className={styles.errorPage}>
                <p>{error || 'ইভেন্ট পাওয়া যায়নি'}</p>
                <button type="button" onClick={() => history.push('/')} className={styles.backBtn}>
                    ← হোমে ফিরুন
                </button>
            </div>
        );
    }

    const statusClass =
        event.status === 'live' ? styles.statusLive : event.status === 'completed' ? styles.statusDone : styles.statusUpcoming;
    const sportClass = event.category === 'Cricket' ? styles.cricket : styles.football;

    return (
        <div className={styles.page}>
            <div className={styles.container}>
                <header className={styles.topBar}>
                    <button type="button" onClick={() => history.goBack()} className={styles.backBtn}>
                        ← ফিরে যান
                    </button>
                    <div className={styles.topActions}>
                        <button type="button" onClick={handleFavorite} className={styles.iconBtn} aria-label="পছন্দ">
                            {isFavorite(event.id) ? '★' : '☆'}
                        </button>
                        <button type="button" onClick={handleShare} className={styles.iconBtn} aria-label="শেয়ার">
                            ↗
                        </button>
                    </div>
                </header>

                <section className={styles.playerSection}>
                    {showPlayer && streamUrl ? (
                        <PlayerIframe src={streamUrl} />
                    ) : (
                        <div className={styles.playerPlaceholder}>
                            <div className={styles.placeholderInner}>
                                <div className={`${styles.sportIconWrap} ${sportClass}`}>
                                    <SportIcon category={event.category} size={36} />
                                </div>
                                <button
                                    type="button"
                                    onClick={handlePlayClick}
                                    disabled={streamLoading}
                                    className={styles.playBtn}
                                    aria-label="স্ট্রিম চালু করুন"
                                >
                                    {streamLoading ? (
                                        <span className={styles.spinnerSmall} />
                                    ) : (
                                        '▶'
                                    )}
                                </button>
                                <p className={styles.playHint}>
                                    {streamLoading ? 'স্ট্রিম লোড হচ্ছে...' : 'প্লে বাটনে ট্যাপ করুন'}
                                </p>
                            </div>
                        </div>
                    )}
                </section>

                {error && (
                    <div className={styles.streamError} role="alert">
                        ⚠️ {error}
                        <button type="button" onClick={handlePlayClick} className={styles.retryLink}>
                            আবার চেষ্টা
                        </button>
                    </div>
                )}

                <div className={styles.content}>
                    <div className={styles.main}>
                        <div className={styles.titleRow}>
                            <h1>{event.name}</h1>
                            <span className={`${styles.statusBadge} ${statusClass}`}>
                                {getStatusLabel(event.status)}
                            </span>
                        </div>

                        <div className={styles.metaGrid}>
                            <div className={styles.metaItem}>
                                <span className={styles.metaLabel}>তারিখ</span>
                                <span>{event.date}</span>
                            </div>
                            <div className={styles.metaItem}>
                                <span className={styles.metaLabel}>সময়</span>
                                <span>{event.time}</span>
                            </div>
                            <div className={styles.metaItem}>
                                <span className={styles.metaLabel}>স্থান</span>
                                <span>{event.location}</span>
                            </div>
                            <div className={styles.metaItem}>
                                <span className={styles.metaLabel}>ধরন</span>
                                <span className={styles.metaValue}>
                                    <SportIcon category={event.category} size={16} />
                                    {getCategoryLabel(event.category)}
                                </span>
                            </div>
                        </div>
                    </div>

                    <aside className={styles.sidebar}>
                        <div className={styles.infoCard}>
                            <h3>স্ট্রিম স্ট্যাটাস</h3>
                            <p className={streamUrl ? styles.active : styles.inactive}>
                                {streamUrl ? '● সক্রিয়' : '○ নিষ্ক্রিয়'}
                            </p>
                        </div>
                        <div className={styles.infoCard}>
                            <h3>ইভেন্ট আইডি</h3>
                            <p className={styles.eventId}>{id}</p>
                        </div>
                        {event.haveLiveIcon && (
                            <div className={styles.infoCard}>
                                <h3>স্ট্রিম</h3>
                                <p className={styles.active}>স্ট্রিম উপলব্ধ হতে পারে</p>
                            </div>
                        )}
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default EventDetail;
